import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMotionPreference } from '../hooks/useMotionPreference';
import '../styles/site-shell.css';
import { usePageChoreography } from '../hooks/usePageChoreography';
import { ImmersiveBackground } from './ImmersiveBackground';

interface PageLayoutProps {
	children: React.ReactNode;
}

export const PageLayout: React.FC<PageLayoutProps> = ({ children }) => {
	const [menuOpen, setMenuOpen] = useState(false);
	const [documentVisible, setDocumentVisible] = useState(true);
	const { motionPaused, reducedMotion, motionDisabled, toggleMotion } = useMotionPreference();
	const shellRef = useRef<HTMLDivElement>(null);
	const location = useLocation();
	const headerRef = useRef<HTMLElement>(null);
	const mobileNavRef = useRef<HTMLElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const footerRef = useRef<HTMLElement>(null);
	const closeMenu = () => setMenuOpen(false);
	usePageChoreography(shellRef, motionDisabled, location.pathname);

	useEffect(() => {
		const updateVisibility = () => setDocumentVisible(!document.hidden);
		updateVisibility();
		document.addEventListener('visibilitychange', updateVisibility);
		return () => document.removeEventListener('visibilitychange', updateVisibility);
	}, []);

	useEffect(() => {
		shellRef.current?.querySelectorAll<SVGSVGElement>('.page-atmosphere svg').forEach((svg) => {
			if (motionDisabled || !documentVisible) svg.pauseAnimations?.();
			else svg.unpauseAnimations?.();
		});
	}, [motionDisabled, documentVisible, location.pathname]);

	useEffect(() => {
		let hash = window.location.hash.slice(1);
		try { hash = decodeURIComponent(hash); } catch { hash = ''; }
		const frame = requestAnimationFrame(() => {
			const section = hash ? document.getElementById(hash) : null;
			if (section) section.scrollIntoView();
			else window.scrollTo(0, 0);
		});
		return () => cancelAnimationFrame(frame);
	}, [location.pathname]);

	useEffect(() => {
		setMenuOpen(false);
	}, [location.key]);

	useEffect(() => {
		if (!menuOpen) return;

		const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
		const previousOverflow = document.body.style.overflow;
		const content = contentRef.current;
		const footer = footerRef.current;
		const desktopQuery = window.matchMedia('(min-width: 861px)');
		const focusableElements = () => Array.from(
			headerRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') ?? [],
		).filter((element) => element.tabIndex >= 0 && element.getClientRects().length > 0);

		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				event.preventDefault();
				setMenuOpen(false);
			}
			if (event.key !== 'Tab') return;
			const elements = focusableElements();
			const first = elements[0];
			const last = elements[elements.length - 1];
			if (event.shiftKey && (document.activeElement === first || !headerRef.current?.contains(document.activeElement))) {
				event.preventDefault();
				last?.focus();
			} else if (!event.shiftKey && (document.activeElement === last || !headerRef.current?.contains(document.activeElement))) {
				event.preventDefault();
				first?.focus();
			}
		};
		const handleDesktop = () => {
			if (desktopQuery.matches) setMenuOpen(false);
		};

		document.body.style.overflow = 'hidden';
		content?.setAttribute('inert', '');
		footer?.setAttribute('inert', '');
		mobileNavRef.current?.querySelector<HTMLAnchorElement>('a')?.focus();
		window.addEventListener('keydown', handleEscape);
		desktopQuery.addEventListener('change', handleDesktop);
		return () => {
			document.body.style.overflow = previousOverflow;
			content?.removeAttribute('inert');
			footer?.removeAttribute('inert');
			window.removeEventListener('keydown', handleEscape);
			desktopQuery.removeEventListener('change', handleDesktop);
			if (previousFocus?.isConnected && previousFocus.getClientRects().length > 0) previousFocus.focus();
		};
	}, [menuOpen]);

	return (
		<div ref={shellRef} className={`internal-shell${motionDisabled ? ' is-motion-paused' : ''}${!documentVisible ? ' is-motion-suspended' : ''}`}>
            <ImmersiveBackground disabled={motionDisabled} />
			<header ref={headerRef} className={`internal-header ${menuOpen ? 'is-menu-open' : ''}`}>
        <span className="internal-reading-progress" aria-hidden="true" />
				<a className="internal-skip-link" href="#internal-main" tabIndex={menuOpen ? -1 : 0}>Skip to content</a>
				<Link className="brand-logo" to="/" aria-label="Cognixis home" onClick={closeMenu}>
					<img className="brand-logo__image" src="/cognixis-logo-site.png" alt="Cognixis" />
				</Link>

				<nav className="internal-nav" aria-label="Site navigation">
					<Link to="/#work">Our work</Link>
					<Link to="/about" aria-current={location.pathname === '/about' ? 'page' : undefined}>Company</Link>
                    <Link to="/#lab">The lab</Link>
					<a className="internal-nav__home" href="https://kairos.cognixisai.com" target="_blank" rel="noopener noreferrer">Explore Kairos <span aria-hidden="true">↗</span></a>
				</nav>

                <button type="button" className="internal-header-motion" aria-label={reducedMotion ? 'Reduced motion enabled by your system' : motionPaused ? 'Resume motion' : 'Pause motion'} aria-pressed={motionDisabled} disabled={reducedMotion} onClick={toggleMotion}><span /><span /><span /></button>

				<button
					type="button"
					className={`internal-menu-toggle ${menuOpen ? 'is-open' : ''}`}
					aria-expanded={menuOpen}
					aria-controls="internal-mobile-navigation"
					aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
					onClick={() => setMenuOpen((open) => !open)}
				>
					<span />
					<span />
				</button>

				{menuOpen && (
					<nav ref={mobileNavRef} id="internal-mobile-navigation" className="internal-mobile-nav is-open" aria-label="Mobile navigation">
						<Link to="/#work" onClick={closeMenu}>Our work <span aria-hidden="true">↗</span></Link>
						<Link to="/#lab" onClick={closeMenu}>The lab <span aria-hidden="true">↗</span></Link>
						<Link to="/about" aria-current={location.pathname === '/about' ? 'page' : undefined} onClick={closeMenu}>Company <span aria-hidden="true">↗</span></Link>
						<a href="https://kairos.cognixisai.com" target="_blank" rel="noopener noreferrer" onClick={closeMenu}>Explore Kairos <span aria-hidden="true">↗</span></a>
					</nav>
				)}
			</header>

			<div ref={contentRef} id="internal-main" className="internal-content" tabIndex={-1}>{children}</div>

			<footer ref={footerRef} className="internal-footer">
				<div className="internal-footer__top">
					<Link className="brand-logo brand-logo--footer" to="/" aria-label="Cognixis home">
						<img className="brand-logo__image" src="/cognixis-logo-site.png" alt="Cognixis" />
					</Link>
					<nav className="internal-footer__company" aria-label="Company links">
						<Link to="/about">Company</Link>
						<Link to="/careers">Careers</Link>
						<Link to="/blog">Ideas</Link>
					</nav>
				</div>
				<div className="internal-footer__bottom">
					<span>© {new Date().getFullYear()} Cognixis LLC</span>
					<button
						type="button"
						className="internal-motion-toggle"
						aria-label={reducedMotion ? 'Reduced motion enabled by your system' : motionPaused ? 'Resume motion' : 'Pause motion'}
						aria-pressed={motionDisabled}
						aria-disabled={reducedMotion || undefined}
						title={reducedMotion ? 'Motion is reduced by your device preference' : undefined}
						onClick={reducedMotion ? undefined : toggleMotion}
					>
						<span aria-hidden="true">{motionDisabled ? '▷' : 'Ⅱ'}</span>
						{reducedMotion ? 'Reduced motion' : motionPaused ? 'Resume motion' : 'Pause motion'}
					</button>
					<nav className="internal-footer__legal" aria-label="Legal links">
						<Link to="/privacy-policy">Privacy</Link>
						<Link to="/terms-of-service">Terms</Link>
						<Link to="/cookie-policy">Cookies</Link><Link to="/ai-disclosure">AI disclosure</Link>
					</nav>
				</div>
			</footer>
		</div>
	);
};
